<script>
    import Button from './Button.svelte';
    import Card from './Card.svelte';
    import H2 from './H2.svelte';
    import Prose from './Prose.svelte';
    import PurchasePaddle from './PurchasePaddle.svelte';
    import { format_age_range } from './utils/format_ages';
    import { img_cdn, img_cdn_srcset } from './utils/image';
    import { logEvent } from './utils/logs';

    /** @type {Course} */
    export let course;
    export let feature = 'courses-list-xlcard';
    export let h_level = 2;
    /** @type {string | undefined} */
    export let title = undefined;
    /** @type {string | undefined} */
    export let lead = undefined;

    let className = '';
    export { className as class };

    $: age_label = format_age_range(course.min_age, course.max_age);

    $: eyebrow_labels = [age_label, ...(course.content_labels || [])].filter(
        Boolean
    );

    $: course_url = `/courses/${course.slug}`;
    $: lessons_url = `/courses/${course.slug}/lessons`;

    /** @type {ImageTransformOptions} */
    const image_options = { w: 309, h: 304, fit: 'contain' };

    function track_first_lesson_click() {
        logEvent(`courses-first-lesson-click-${feature}`, {
            slug: course.slug
        });
    }

    function track_continue_click() {
        logEvent(`courses-continue-click-${feature}`, { slug: course.slug });
    }
</script>

<Card
    class="relative @container @lg:min-h-80 {className}"
    href={course_url}
    click_event={{
        name: `courses-card-click-${feature}`,
        data: { slug: course.slug }
    }}
    aria_label={course.title}
>
    <div
        class="relative z-10 flex flex-col gap-6 h-full pb-32 @lg:pb-0 pointer-events-none"
    >
        <div class="flex flex-wrap gap-2 pr-12 @lg:pr-16">
            {#each eyebrow_labels as label}
                <span
                    class="text-sm bg-surface-highlight rounded-full px-3 py-1 text-nowrap"
                >
                    {label}
                </span>
            {/each}
        </div>

        <div class="space-y-2 @lg:mt-auto">
            <H2 tag={`h${h_level}`} class="link link-el @lg:pr-[35%]">
                {title || course.title}
            </H2>

            <Prose lg class="mb-4 prose-p:leading-normal max-w-none @lg:pr-[40%]">
                {@html lead || course.lead}
            </Prose> 

            {#if course.purchased}
                <div class="@lg:pr-[40%] relative pointer-events-auto">
                    <Button
                        primary
                        outline
                        route={lessons_url}
                        on:click={track_continue_click}
                    >
                        Continue Your Journey
                    </Button>
                </div>
            {:else}
                <div
                    class="flex flex-col items-start gap-3 @lg:flex-row @lg:items-center @lg:gap-4 @lg:flex-wrap @lg:pr-[40%] @3xl:gap-6 pointer-events-auto"
                >
                    <PurchasePaddle
                        product={course}
                        style="small"
                        cta="Buy Now"
                        cta_free="Get for Free"
                        {feature}
                    >
                        <svelte:fragment
                            slot="after"
                            let:total_discount_percent
                            let:is_free
                        >
                            {#if total_discount_percent > 0}
                                <span
                                    class="
                                        text-lg font-bold bg-purple-600
                                        rounded-l-full
                                        py-1 pl-3 pr-1
                                        absolute
                                        -top-0.5 -right-4 md:-right-6 z-20"
                                >
                                    {#if is_free}
                                        It's a gift!
                                    {:else}
                                        &minus;{total_discount_percent}%
                                    {/if}
                                </span>
                            {/if}
                        </svelte:fragment>
                    </PurchasePaddle>
                    <a
                        class="link relative"
                        href={lessons_url}
                        on:click={track_first_lesson_click}
                    >
                        Try first lesson
                    </a>
                </div>
            {/if}
        </div>
    </div>

    {#if course.big_side_image}
        <img
            src={img_cdn(course.big_side_image, image_options)}
            srcset={img_cdn_srcset(course.big_side_image, image_options)}
            alt={course.title}
            width="309"
            height="304"
            loading="lazy"
            class="absolute bottom-0 right-0 z-0 w-56 @lg:w-[42%] @xl:w-[45%] max-h-full object-contain object-bottom-right pointer-events-none"
        />
    {/if}
</Card>
